import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Clock, Loader2, Mic } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatInterface } from '~/components/features/screening/chat-interface';
import { CompletionScreen } from '~/components/features/screening/completion-screen';
import { DeviceTest } from '~/components/features/screening/device-test';
import { LandingPage } from '~/components/features/screening/landing-page';
import { LanguageSelect } from '~/components/features/screening/language-select';
import { VoiceInterface } from '~/components/features/screening/voice-interface';
import { Button } from '~/components/ui/button';
import type { InterviewMessageResponse, SessionInfo } from '~/lib/interview-api';
import {
  InterviewApiError,
  fetchMessages,
  fetchSessionInfo,
  restartInterview,
  startSession
} from '~/lib/interview-api';

export const Route = createFileRoute('/interview/$token')({
  component: InterviewPage
});

type PageState =
  | { step: 'loading' }
  | { step: 'landing'; session: SessionInfo }
  | { step: 'language_select'; session: SessionInfo; modality: 'text' | 'voice' }
  | { step: 'device_test'; session: SessionInfo; language: string }
  | { step: 'voice_interview'; session: SessionInfo; language: string }
  | { step: 'voice_in_progress'; language: string }
  | {
      step: 'chatting';
      session: SessionInfo;
      messages: InterviewMessageResponse[];
    }
  | { step: 'completed'; session: SessionInfo }
  | { step: 'expired' }
  | { step: 'not_found' }
  | { step: 'error'; message: string };

function InterviewPage(): React.ReactNode {
  const { token } = Route.useParams();
  const { t } = useTranslation();
  const [state, setState] = useState<PageState>({ step: 'loading' });
  const [startingSession, setStartingSession] = useState(false);
  const reloadKeyRef = useRef(0);
  const [reloadKey, setReloadKey] = useState(0);
  const autoStartRef = useRef<{ language: string; modality: string } | null>(null);

  const handleRestart = useCallback(() => {
    if (state.step === 'chatting') {
      autoStartRef.current = { language: state.session.language ?? 'en', modality: 'text' };
    } else if (state.step === 'voice_interview') {
      autoStartRef.current = { language: state.language, modality: 'voice' };
    }
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, [state]);

  const handleVoiceRetry = useCallback(async () => {
    if (state.step !== 'voice_in_progress') return;
    const language = state.language;
    try {
      await restartInterview(
        token,
        'Candidate closed browser and requested retry',
        'candidate_retry'
      );
    } catch {
      // If restart fails (e.g. session already gone), still proceed to reload
    }
    autoStartRef.current = { language, modality: 'voice' };
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, [state, token]);

  // Load session info on mount and after restart
  useEffect(() => {
    let cancelled = false;
    setState({ step: 'loading' });

    async function load(): Promise<void> {
      try {
        const session = await fetchSessionInfo(token);

        if (cancelled) return;

        if (session.status === 'completed') {
          setState({ step: 'completed', session });
        } else if (session.status === 'in_progress') {
          if (session.modality === 'voice') {
            // Voice interviews cannot be reconnected from another tab
            setState({ step: 'voice_in_progress', language: session.language ?? 'en' });
          } else {
            // Text: reconnect — load existing messages
            const { messages } = await fetchMessages(token);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cancelled may be set by cleanup during await
            if (cancelled) return;
            setState({ step: 'chatting', session, messages });
          }
        } else {
          // status === null — no session yet, or all sessions failed
          // Auto-start if resuming from a restart, otherwise show landing page
          const autoStart = autoStartRef.current;
          if (autoStart) {
            autoStartRef.current = null;
            if (autoStart.modality === 'voice') {
              setState({ step: 'device_test', session, language: autoStart.language });
            } else {
              setStartingSession(true);
              try {
                const { messages } = await startSession(token, autoStart.language);
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cancelled may be set by cleanup during await
                if (cancelled) return;
                setState({ step: 'chatting', session, messages });
              } catch (err) {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cancelled may be set by cleanup during await
                if (cancelled) return;
                if (err instanceof InterviewApiError) {
                  setState({ step: 'error', message: err.message });
                } else {
                  setState({ step: 'error', message: t('screening.error.message') });
                }
              } finally {
                setStartingSession(false);
              }
            }
          } else {
            setState({ step: 'landing', session });
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof InterviewApiError) {
          if (err.status === 404) {
            setState({ step: 'not_found' });
          } else if (err.status === 410) {
            setState({ step: 'expired' });
          } else {
            setState({ step: 'error', message: err.message });
          }
        } else {
          setState({ step: 'error', message: t('screening.error.message') });
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [token, t, reloadKey]);

  const handleStart = useCallback(
    async (modality: 'text' | 'voice') => {
      if (state.step !== 'landing') return;
      const { session } = state;

      // Auto-skip language selection when only one language (or none configured)
      const languages = session.job.languages;
      if (languages.length > 1) {
        setState({ step: 'language_select', session, modality });
        return;
      }

      const language = languages[0] ?? 'en';

      if (modality === 'voice') {
        setState({ step: 'device_test', session, language });
        return;
      }

      // Text with single language: start session immediately
      setStartingSession(true);
      try {
        const { messages } = await startSession(token, language);
        setState({ step: 'chatting', session, messages });
      } catch (err) {
        if (err instanceof InterviewApiError) {
          setState({ step: 'error', message: err.message });
        } else {
          setState({ step: 'error', message: t('screening.error.message') });
        }
      } finally {
        setStartingSession(false);
      }
    },
    [state, token, t]
  );

  const handleLanguageSelect = useCallback(
    async (language: string) => {
      if (state.step !== 'language_select') return;

      if (state.modality === 'voice') {
        setState({ step: 'device_test', session: state.session, language });
        return;
      }

      // For text: start session immediately
      setStartingSession(true);
      try {
        const { messages } = await startSession(token, language);
        setState({ step: 'chatting', session: state.session, messages });
      } catch (err) {
        if (err instanceof InterviewApiError) {
          setState({ step: 'error', message: err.message });
        } else {
          setState({ step: 'error', message: t('screening.error.message') });
        }
      } finally {
        setStartingSession(false);
      }
    },
    [state, token, t]
  );

  const handleDeviceTestConfirm = useCallback(() => {
    if (state.step !== 'device_test') return;
    setState({
      step: 'voice_interview',
      session: state.session,
      language: state.language
    });
  }, [state]);

  const handleCompleted = useCallback(() => {
    if (state.step === 'chatting' || state.step === 'voice_interview') {
      setState({ step: 'completed', session: state.session });
    }
  }, [state]);

  switch (state.step) {
    case 'loading':
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('screening.loading')}</p>
          </div>
        </div>
      );

    case 'voice_in_progress':
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <div className="w-full max-w-sm">
            <div className="rounded-2xl border border-border bg-card px-8 py-10 shadow-sm">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="flex size-14 items-center justify-center rounded-full bg-muted">
                  <Mic className="size-7 text-muted-foreground" />
                </div>
                <h1 className="text-xl">{t('screening.voice.alreadyInProgress')}</h1>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t('screening.voice.alreadyInProgressMessage')}
                </p>
                <Button
                  className="mt-2 w-full"
                  onClick={() => {
                    void handleVoiceRetry();
                  }}
                >
                  {t('screening.voice.retryButton')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      );

    case 'landing':
      return (
        <LandingPage
          session={state.session}
          loading={startingSession}
          onStart={(modality) => {
            void handleStart(modality);
          }}
        />
      );

    case 'language_select':
      return (
        <LanguageSelect
          languages={state.session.job.languages.length > 0 ? state.session.job.languages : ['en']}
          loading={startingSession}
          onSelect={(lang) => {
            void handleLanguageSelect(lang);
          }}
        />
      );

    case 'device_test':
      return <DeviceTest onConfirm={handleDeviceTestConfirm} />;

    case 'voice_interview':
      return (
        <VoiceInterface
          token={token}
          language={state.language}
          jobTitle={state.session.job.title}
          employer={state.session.job.employer}
          candidateName={state.session.candidate.name}
          onCompleted={handleCompleted}
        />
      );

    case 'chatting':
      return (
        <ChatInterface
          token={token}
          jobTitle={state.session.job.title}
          employer={state.session.job.employer}
          initialMessages={state.messages}
          onCompleted={handleCompleted}
          onRestart={handleRestart}
        />
      );

    case 'completed':
      return (
        <CompletionScreen
          jobTitle={state.session.job.title}
          token={token}
          feedbackSubmitted={state.session.feedbackSubmitted}
        />
      );

    case 'expired':
      return (
        <StatusPage
          icon={<Clock className="size-7 text-muted-foreground" />}
          title={t('screening.expired.title')}
          message={t('screening.expired.message')}
        />
      );

    case 'not_found':
      return (
        <StatusPage
          icon={<AlertCircle className="size-7 text-muted-foreground" />}
          title={t('screening.notFound.title')}
          message={t('screening.notFound.message')}
        />
      );

    case 'error':
      return (
        <StatusPage
          icon={<AlertCircle className="size-7 text-destructive" />}
          title={t('screening.error.title')}
          message={state.message}
        />
      );
  }
}

function StatusPage({
  icon,
  title,
  message
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
}): React.ReactNode {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="rounded-2xl border border-border bg-card px-8 py-10 shadow-sm">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex size-14 items-center justify-center rounded-full bg-muted">
              {icon}
            </div>
            <h1 className="text-xl">{title}</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">{message}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
